/** Hook for persisting product table configuration to localStorage. */
import { useState, useCallback } from 'react'
import type { SortingState, VisibilityState } from '@tanstack/react-table'

const STORAGE_KEY = 'goki:product-table-config'

export type ProductTableFilters = {
  is_active?: boolean
  quantity_30d_min?: number
  revenue_30d_min?: number
  quantity_90d_min?: number
  revenue_90d_min?: number
  has_image?: boolean
  synced_after?: string
}

type StoredConfig = {
  columnVisibility: VisibilityState; sorting: SortingState; filters: ProductTableFilters; pageSize: number
}

const DEFAULTS: StoredConfig = {
  columnVisibility: { gtin: false, quantity_90d: false, revenue_90d: false, readiness_score: false, issues_count: false },
  sorting: [], filters: { is_active: true }, pageSize: 25,
}

function load(): StoredConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const stored = JSON.parse(raw) as StoredConfig
      // Ensure is_active defaults to true if not explicitly set (e.g. old localStorage without the key)
      if (stored.filters?.is_active === undefined) {
        stored.filters = { ...stored.filters, is_active: true }
      }
      return stored
    }
  } catch { /* ignore */ }
  return DEFAULTS
}

export function useProductTableConfig() {
  const [cfg, setCfg] = useState<StoredConfig>(load)
  const update = useCallback((patch: Partial<StoredConfig>) => {
    setCfg((prev) => {
      const next = { ...prev, ...patch }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      return next
    })
  }, [])
  return {
    ...cfg,
    setColumnVisibility: (v: VisibilityState) => update({ columnVisibility: v }),
    setSorting: (s: SortingState) => update({ sorting: s }),
    setFilters: (f: ProductTableFilters) => update({ filters: f }),
    setPageSize: (n: number) => update({ pageSize: n }),
    resetFilters: () => update({ filters: DEFAULTS.filters }),
  }
}
